import { GROUPS, TOP_LEVEL } from './navItems.js';

/* ===========================================================================
 * PILLAR ACCENTS — "a touch of colour", scoped so it cannot become decoration.
 *
 * One hue per top-level area of the product, appearing in EXACTLY TWO PLACES:
 * the rail's active marker, and a 3px rule under that area's page title. Never
 * on a control, never as a fill, never behind text.
 *
 * THE RESTRAINT IS THE DESIGN, not a hedge. `--primary` currently means one
 * thing in this app — "this is the action" — and that single meaning is why a
 * console this dense reads as calm. A hue that also turned up on buttons would
 * spend it. Confined to chrome, the accent answers a different question
 * ("where am I?") and so cannot compete with the brand or with the status
 * families, which are the two colour systems already carrying meaning here.
 *
 * PEOPLE IS THE BRAND, deliberately — the roster is the product's centre, so
 * that pillar is `--primary` rather than a sixth invented hue.
 *
 * The values, their two forms (on paper, and lifted for the ink rail) and the
 * measured contrast for each are in apps/web/src/index.css. Read that before
 * changing one: they were levelled against each other so no area's rule reads
 * heavier than another's, and a hue that drifts light stops being a family
 * member and becomes an exception.
 * ======================================================================== */
export type Pillar = 'people' | 'attendance' | 'sites' | 'reports' | 'admin';

/* ---------------------------------------------------------------------------
 * WHICH PILLAR A PATH BELONGS TO — one resolver, read by both places the
 * accent appears.
 *
 * It has to be ONE function rather than two lookups because the rail and the
 * page header would otherwise be free to disagree, and a rule under the title
 * in a different colour from the row you clicked to get there is worse than no
 * accent at all: it says the two are unrelated.
 *
 * LONGEST PREFIX WINS, so `/reports/workspace` resolves to reports rather than
 * matching a shorter route first, and a sub-page nobody tagged inherits its
 * parent's accent instead of falling through to the default.
 * ------------------------------------------------------------------------ */
const PILLAR_ROUTES: ReadonlyArray<readonly [string, Pillar]> = [
  ...TOP_LEVEL.flatMap((leaf) =>
    leaf.pillar ? ([[leaf.to, leaf.pillar]] as const) : []
  ),
  ...GROUPS.flatMap((group) =>
    group.items.flatMap((leaf) => {
      const pillar = leaf.pillar ?? group.pillar;
      return pillar ? ([[leaf.to, pillar]] as const) : [];
    })
  ),
];

/**
 * The pillar for a pathname, or `'people'` — which IS the brand — when nothing
 * matches. The fallback is deliberate rather than a `null` every caller has to
 * branch on: an untagged screen gets the accent it would have had before
 * pillars existed, so adding a route can never make one look broken.
 */
export function pillarForPath(pathname: string): Pillar {
  let best: Pillar = 'people';
  let bestLen = -1;
  for (const [to, pillar] of PILLAR_ROUTES) {
    if ((pathname === to || pathname.startsWith(`${to}/`)) && to.length > bestLen) {
      best = pillar;
      bestLen = to.length;
    }
  }
  return best;
}

/** The rail's marker ink per pillar — the lifted form, for the ink rail. */
export const PILLAR_NAV_CLASS: Record<Pillar, string> = {
  people: 'bg-pillar-people-nav',
  attendance: 'bg-pillar-attendance-nav',
  sites: 'bg-pillar-sites-nav',
  reports: 'bg-pillar-reports-nav',
  admin: 'bg-pillar-admin-nav',
};

/** The active row's GLYPH ink per pillar. Same lifted values as the marker —
 *  the row's state lives in the icon as well as the bar beside it, because at
 *  the collapsed width the bar is most of what is left. */
export const PILLAR_ICON_CLASS: Record<Pillar, string> = {
  people: 'text-pillar-people-nav',
  attendance: 'text-pillar-attendance-nav',
  sites: 'text-pillar-sites-nav',
  reports: 'text-pillar-reports-nav',
  admin: 'text-pillar-admin-nav',
};
